import { issuedCredentialsSchema, loginResponseSchema, passwordResetAvailabilitySchema, recordInspectionStepSchema, refreshResponseSchema, } from '@ghp/domain';
import { createClientLogsEndpoints } from './client-logs.js';
import { createClientsEndpoints } from './clients.js';
import { createDashboardEndpoints } from './dashboard.js';
import { bearer, createHttp } from './http.js';
import { createExportsEndpoints } from './exports.js';
import { createInquiriesEndpoints } from './inquiries.js';
import { createMunicipalitiesEndpoints } from './municipalities.js';
import { createMeEndpoints } from './me.js';
import { createPassageNoticesEndpoints } from './passage-notices.js';
import { createSettingsEndpoints } from './settings.js';
import { createPlanningEndpoints } from './planning.js';
import { createSyncEndpoints } from './sync.js';
import { createTeamsEndpoints } from './teams.js';
import { createUsersEndpoints } from './users.js';
import { createValidationEndpoints } from './validation.js';
import { createVocabularyEndpoints } from './vocabulary.js';
import { createReportRulesEndpoints } from './report-rules.js';
// L'URL de l'API est ce qu'on passe à `createApiClient` : sa lecture vit donc ici, une fois,
// plutôt qu'une fois par front — la règle qu'elle porte (D23) est trop facile à réécrire de
// travers pour être recopiée.
export { readApiBaseUrl } from './api-base-url.js';
export { ApiError, DownloadInterruptedError } from './http.js';
export { serverMessage } from './server-message.js';
/**
 * Client HTTP typé de l'API, partagé back-office/terrain (D21). Écrit contre les contrats de
 * `packages/domain` : toute réponse est validée par Zod — une API qui ment échoue ici, pas
 * plus loin.
 *
 * Douze familles d'appels. `auth` s'appuie sur le cookie ou le corps selon le front (D3) ;
 * `sync`, `inquiries`, `municipalities`, `planning`, `clients`, `teams`, `users`, `validation`,
 * `exports`, `vocabulary` et `settings` présentent le jeton d'accès en en-tête (M23) — celui de
 * l'équipe côté tablette, celui de la session côté back-office. Le transport est commun
 * (`http.ts`).
 */
export function createApiClient(options) {
    const http = createHttp(options);
    return {
        auth: {
            login(request) {
                return http.postJson('/auth/login', request, loginResponseSchema);
            },
            // Pas de verrou single-flight : le refresh n'est pas invalidé à l'usage (pas de
            // rotation au démarrage, cf. `refreshResponseSchema` dans `@ghp/domain`, M23) — des
            // appels concurrents obtiennent chacun un jeton neuf sans s'éjecter mutuellement.
            refresh(request = {}) {
                return http.postJson('/auth/refresh', request, refreshResponseSchema);
            },
            async logout(request = {}) {
                await http.post('/auth/logout', request);
            },
            // Un administrateur peut couper « Mot de passe oublié » : l'écran le demande d'abord.
            passwordResetAvailability() {
                return http.get('/password-reset', passwordResetAvailabilitySchema);
            },
            // « Mot de passe oublié » (D3) : `202` que le compte existe ou non — rien à lire en retour.
            async requestPasswordReset(request) {
                await http.post('/password-reset', request);
            },
            // Le lien reçu par e-mail tire le mot de passe, **en clair, une seule fois**.
            confirmPasswordReset(request) {
                return http.postJson('/password-reset/confirm', request, issuedCredentialsSchema);
            },
        },
        // L'export client (M26) : la file de ce qui est dû, et la livraison qui laisse sa trace.
        exports: createExportsEndpoints(http),
        inquiries: createInquiriesEndpoints(http),
        // Les avis de passage (le constat d'un passage sans contrôle) : leur propre chemin HTTP, même
        // si l'API les sert depuis le module `inquiries` — cf. `PassageNoticesController`.
        passageNotices: createPassageNoticesEndpoints(http),
        // L'étape d'un contrôle (M42) — commencé, signé — que la tablette dépose depuis sa file.
        inspectionSteps: {
            record(accessToken, input) {
                return http.postJson('/inspection-steps', input, recordInspectionStepSchema, bearer(accessToken));
            },
        },
        // Le référentiel des communes (M8) : ce dans quoi le formulaire de création choisit une
        // adresse. Lecture seule — les codes INSEE sont administrés par l'État, pas saisis.
        municipalities: createMunicipalitiesEndpoints(http),
        // Le répertoire des donneurs d'ordre (écran « Clients & lots ») : les clients, leurs lots et
        // l'avancement de leurs contrôles.
        clients: createClientsEndpoints(http),
        // Le tableau de bord (R-B1) : ce que l'écran d'accueil lit à travers les domaines.
        dashboard: createDashboardEndpoints(http),
        // Le planning (M24) : poser un rendez-vous, et savoir si la confirmation est partie.
        planning: createPlanningEndpoints(http),
        // Les réglages de l'organisation (écran « Réglages ») : l'identité de GHP et les paramètres
        // de ses documents. Ni domaine métier ni gestion de comptes — du paramétrage, transverse.
        settings: createSettingsEndpoints(http),
        sync: createSyncEndpoints(http),
        // Ce que le compte connecté règle pour lui-même — son paraphe d'agent.
        me: createMeEndpoints(http),
        teams: createTeamsEndpoints(http),
        users: createUsersEndpoints(http),
        validation: createValidationEndpoints(http),
        // Le référentiel d'anomalies administré depuis « Règles anomalies » (M17). Distinct de
        // `sync.listVocabulary`, qui sert à la tablette ce qu'un agent peut encore cocher.
        vocabulary: createVocabularyEndpoints(http),
        // Le journal de diagnostic des tablettes (D15). Ni donnée métier, ni lecture d'écran :
        // la tablette y pousse ses lots, un admin les relit pour comprendre une tournée.
        clientLogs: createClientLogsEndpoints(http),
        // Les règles du rapport client (M33), administrées depuis « Gestion du rapport PDF » : quels
        // blocs disparaissent du PDF livré, par secteur ou par client. Du paramétrage de ce qui part au
        // client, pas une donnée de contrôle — d'où sa place à côté de `settings` plutôt que dans
        // `validation`.
        reportRules: createReportRulesEndpoints(http),
    };
}
//# sourceMappingURL=index.js.map